import React from "react";
import { View, Text } from "react-native";
import { FreeplayEntry, FreeplayEvent, FreeplayLeague, FreeplayUser } from "@freeplay/shared-code";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GameButton } from "../components/textcontainers/home/GameButton";
import { GlobalStyles } from "../components/style/Style";
import { MediaQueries } from "../components/hooks/dynamicStyleHook";
import { useServices } from "../contexts/ServiceContext";
import { format } from "date-fns";
import DefaultImage from "../assets/default_background.png";
import DefaultTSImage from "../assets/default_background_ts.png";
import { ButtonSize, ButtonTheme, RoundedButton } from "../components/buttons/roundedButton/RoundedButton";

interface MyPicksViewProps {
    fpUser: FreeplayUser | undefined;
    playerEventHistory: PickHistory[];
    showPicksOverview: (event: FreeplayEvent) => void;
    showHome: () => void;
}

export const MyPicksView: React.FC<MyPicksViewProps> = ({
    fpUser,
    playerEventHistory,
    showPicksOverview,
    showHome,
}) => {
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(generateStyles);

    if (!fpUser) {
        return (
            <View style={[edgeDeploy ? styles.edgeContainer : styles.container, styles.emptyContainer]}>
                <Text style={styles.emptyText}>Login to make picks!</Text>
            </View>
        );
    }

    if (!playerEventHistory) {
        return (
            <View style={[edgeDeploy ? styles.edgeContainer : styles.container, styles.emptyContainer]}>
                <Text style={styles.emptyText}>Go make your first picks!</Text>
            </View>
        );
    }

    const containers: JSX.Element[] = [];

    playerEventHistory.forEach((localEntry: PickHistory, index: number) => {
        containers.push(
            <GameButton
                imageUri={localEntry.event.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage} // TODO change this back if we ever use custom images
                title={localEntry.event.name}
                timeLine={format(new Date(localEntry.event.bettingEnds), "LLLL d, h:mm b")}
                prizeString={"0"}
                ctaLabel={"View Picks"}
                onMyPicksScreen={true}
                last={index === playerEventHistory.length - 1}
                onPress={() => showPicksOverview(localEntry.event)}
                key={index}
            />,
        );
    });

    return (
        <View style={edgeDeploy ? styles.edgeContainer : styles.container}>
            {containers.length > 0 ? (
                containers
            ) : (
                <View style={styles.noPicksContainer}>
                    <Text style={styles.checkBackLater}>You have not made picks yet!</Text>
                    <RoundedButton
                        config={{
                            buttonText: "Make Picks",
                            size: ButtonSize.LARGE,
                            width: 250,
                            height: 50,
                            theme: ButtonTheme.CTA,
                            margin: { bottom: 0 },
                        }}
                        testID={`next-game-button`}
                        onPress={() => showHome()}
                    />
                </View>
            )}
        </View>
    );
};

export type PickHistory = FreeplayEntry & {
    event: FreeplayEvent;
};

const generateStyles = () => {
    return EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
        },
        [MediaQueries.SMALL]: {
            edgeBottomButtonsContainer: {
                height: 90,
            },
        },
        edgeContainer: {
            flex: 1,
            flexWrap: "wrap",
            flexDirection: "row",
            padding: 15,
            marginVertical: 30,
            overflowY: "auto",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
        },
        container: {
            flex: 1,
            padding: 10,
            paddingTop: 0,
            overflowY: "auto",
        },
        emptyText: {
            fontSize: 32,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            alignSelf: "center",
            textAlign: "center",
        },
        emptyContainer: {
            justifyContent: "center",
            alignItems: "center",
        },
        checkBackLater: {
            fontSize: 18,
            marginTop: 5,
            fontWeight: "600",
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            textAlign: "center",
            marginBottom: 10,
        },
        noPicksContainer: {
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
        },
    });
};
